/**
 * 设置页（D-40，统一 Settings UI）。把散落的偏好归口：自动保存间隔/默认画质/撤销栈深度/
 * AI 水印默认/主题。全部走 settingsStore 持久化（不散写 localStorage）。
 * 入口：用户信息面板「更多设置」。
 */
import { RotateCcw } from 'lucide-react';

import { DEFAULT_SETTINGS, useSettingsStore, type AppSettings } from '../settingsStore';

const sel = 'rounded-control bg-surface-base px-2.5 py-1.5 text-uiSm text-ink-primary outline-none';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="panel flex flex-col gap-3 p-4">
      <h2 className="text-ui font-semibold text-ink-primary">{title}</h2>
      {children}
    </section>
  );
}

function Item({ label, hint, control }: { label: string; hint?: string; control: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <div className="text-ui text-ink-primary">{label}</div>
        {hint !== undefined && <div className="text-uiSm text-ink-faint">{hint}</div>}
      </div>
      <div className="shrink-0">{control}</div>
    </div>
  );
}

export function SettingsPage() {
  const s = useSettingsStore();

  return (
    <div className="relative h-full overflow-y-auto">
      <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col gap-4 px-6 py-8">
        <header className="mb-1 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">设置</h1>
            <p className="mt-1 text-ui text-ink-muted">应用偏好，本地持久化（不随单个工程走）。</p>
          </div>
          <button type="button" onClick={s.resetSettings} className="btn-ghost inline-flex items-center gap-1.5 px-3 py-1.5 text-ui">
            <RotateCcw size={14} />
            恢复默认
          </button>
        </header>

        <Section title="数据与保存">
          <Item
            label="自动保存间隔"
            hint="工程变更后每隔多久落盘（本地 + 服务器文件）"
            control={
              <select className={sel} value={s.autosaveIntervalSec} onChange={(e) => s.setAutosaveInterval(Number(e.target.value))}>
                {[10, 30, 60, 300].map((v) => (
                  <option key={v} value={v}>{`${v} 秒`}</option>
                ))}
              </select>
            }
          />
          <Item
            label="撤销 / 重做栈深度"
            hint={`默认 ${DEFAULT_SETTINGS.historyDepth}，范围 20–500`}
            control={<input type="number" min={20} max={500} value={s.historyDepth} onChange={(e) => s.setHistoryDepth(Number(e.target.value))} className={`${sel} w-24`} />}
          />
        </Section>

        <Section title="导演台">
          <Item
            label="默认画质"
            hint="影响视口抗锯齿/网格/分辨率档"
            control={
              <select className={sel} value={s.defaultQuality} onChange={(e) => s.setDefaultQuality(e.target.value as AppSettings['defaultQuality'])}>
                <option value="low">流畅（低）</option>
                <option value="medium">均衡（中）</option>
                <option value="high">精细（高）</option>
              </select>
            }
          />
        </Section>

        <Section title="AI 生成">
          <Item
            label="AI 生成默认加水印"
            hint="合规要求：生成的图片/视频默认叠加 AI 水印"
            control={
              <button type="button" onClick={() => s.setWatermark(!s.watermark)} className={`h-6 w-11 rounded-full transition-colors ${s.watermark ? 'bg-accent' : 'bg-surface-line'}`}>
                <span className={`block h-6 w-6 rounded-full bg-white transition-transform ${s.watermark ? 'translate-x-5' : ''}`} />
              </button>
            }
          />
        </Section>
      </div>
    </div>
  );
}
